import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { useEntities } from "@hooks/useEntities";
import { useCategories, CategoryStructure } from "@hooks/useCategories";
import { CategoryCombobox } from "@components/transactions/TransactionSplitV2/CategoryCombobox";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { TransferRuleDirection } from "@common/types/transactionRule";
import {
    FormRuleConditionType,
    FormRuleActionType,
} from "../../TransactionRuleForm/constants";
import { TransferRuleFormSchema } from "../constants";

interface Props {
    transferDirection: TransferRuleDirection;
    index: number;
}

export const CategoryActionForm: React.FC<Props> = ({
    transferDirection,
    index,
}) => {
    const entities = useEntities();
    const { values, setFieldValue } =
        useFormikContext<TransferRuleFormSchema>();
    const allCategories = useCategories({ structure: CategoryStructure.FLAT });

    const showOnlyPersonal: boolean = useMemo(() => {
        const entityCondition = values.conditions[transferDirection].find(
            (condition) =>
                condition.conditionType === FormRuleConditionType.ENTITY,
        );

        return !!entityCondition?.entityValue?.some(
            (entityId) =>
                entities.find((entity) => entity.id === entityId)?.isPersonal,
        );
    }, [values.conditions, entities, transferDirection]);

    const categories = useMemo(
        () =>
            showOnlyPersonal
                ? allCategories.filter((c) => !c.isBusiness)
                : allCategories,
        [allCategories, showOnlyPersonal],
    );

    const categoriesById = useCategoriesById();
    const action = values.actions[transferDirection]?.[index];
    const actionCategoryId = action?.value?.categoryId;
    const selectedCategory = actionCategoryId
        ? categoriesById[actionCategoryId]
        : undefined;

    return (
        <CategoryCombobox
            categories={categories}
            selectedCategory={selectedCategory}
            triggerButtonProps={{
                variant: "primary",
                block: false,
            }}
            onChange={(value) => {
                void setFieldValue(
                    `actions.${transferDirection}.${index}.value`,
                    { categoryId: value?.id },
                );
                void setFieldValue(
                    `actions.${transferDirection}.${index}.actionType`,
                    FormRuleActionType.CATEGORY,
                );
            }}
            hideFooter
        />
    );
};
